import React, { useState, useEffect, useCallback } from 'react';
import { SUBCATEGORIES } from '../../constants/subcategories';
import {
  fetchGroups,
  createNewGroup,
  updateGroupById,
  removeGroup,
  detachProductFromGroup,
} from '../../services/groupService';
import ProductThumbnail from './ProductThumbnail';
import GroupProductPickerModal from './GroupProductPickerModal';

function GroupsTab({ seller, loadGroups }) {
  const [groups, setGroups] = useState([]);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupCategory, setNewGroupCategory] = useState('');
  const [newGroupSubcategory, setNewGroupSubcategory] = useState('');
  const [editingGroup, setEditingGroup] = useState(null);
  const [editName, setEditName] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [editSubcategory, setEditSubcategory] = useState('');
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [showProductPicker, setShowProductPicker] = useState(false);
  const [creating, setCreating] = useState(false); // блокировка кнопки

  const load = useCallback(async () => {
    try {
      const data = await fetchGroups(seller.id);
      setGroups(Array.isArray(data) ? data : []);
      if (loadGroups) loadGroups();
    } catch (err) {
      console.error('Ошибка загрузки групп:', err);
    }
  }, [seller.id, loadGroups]);

  useEffect(() => {
    load();
  }, [load]);

  const handleNewCategoryChange = (cat) => {
    setNewGroupCategory(cat);
    setNewGroupSubcategory('');
  };

  const handleEditCategoryChange = (cat) => {
    setEditCategory(cat);
    setEditSubcategory('');
  };

  const handleCreateGroup = async () => {
    const trimmedName = newGroupName.trim();
    if (!trimmedName || !newGroupCategory || !newGroupSubcategory) {
      alert('Введите название, выберите категорию и подкатегорию');
      return;
    }

    // Проверка на дубликат имени у этого продавца
    const alreadyExists = groups.some(
      (g) => g.name.toLowerCase() === trimmedName.toLowerCase()
    );
    if (alreadyExists) {
      alert('Группа с таким названием уже существует');
      return;
    }

    setCreating(true);
    try {
      await createNewGroup(seller.id, trimmedName, newGroupSubcategory);
      setNewGroupName('');
      setNewGroupCategory('');
      setNewGroupSubcategory('');
      await load(); // обновляем список
    } catch (err) {
      const msg = err.response?.data?.error || 'Ошибка при создании группы';
      alert(msg);
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm('Удалить группу? Товары будут откреплены.')) return;
    try {
      await removeGroup(groupId, seller.id);
      await load();
    } catch (err) {
      alert(err.response?.data?.error || 'Ошибка удаления группы');
    }
  };

  const startEditGroup = (group) => {
    setEditingGroup(group);
    setEditName(group.name);
    let foundCat = '';
    for (const cat of Object.keys(SUBCATEGORIES)) {
      if (SUBCATEGORIES[cat].includes(group.subcategory)) {
        foundCat = cat;
        break;
      }
    }
    setEditCategory(foundCat);
    setEditSubcategory(group.subcategory || '');
  };

  const handleUpdateGroup = async () => {
    if (!editingGroup || !editName.trim() || !editCategory || !editSubcategory) {
      alert('Название, категория и подкатегория обязательны');
      return;
    }
    try {
      await updateGroupById(editingGroup.id, seller.id, {
        name: editName.trim(),
        subcategory: editSubcategory,
      });
      setEditingGroup(null);
      await load();
    } catch (err) {
      alert(err.response?.data?.error || 'Ошибка обновления группы');
    }
  };

  const handleRemoveProductFromGroup = async (productId) => {
    if (!window.confirm('Удалить товар из группы?')) return;
    try {
      await detachProductFromGroup(productId, seller.id);
      alert('Товар удалён из группы');
      await load();
    } catch (err) {
      alert('Ошибка: ' + (err.response?.data?.error || err.message));
    }
  };

  const subcategoryOptionsForNew = SUBCATEGORIES[newGroupCategory] || [];
  const subcategoryOptionsForEdit = SUBCATEGORIES[editCategory] || [];

  return (
    <div>
      <h2 style={{ fontWeight: 700, color: '#1e293b', marginBottom: 20 }}>
        Группы товаров (Склейки)
      </h2>

      {/* Форма создания */}
      <div
        style={{
          background: 'white',
          borderRadius: 12,
          padding: 16,
          marginBottom: 20,
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        }}
      >
        <h3 style={{ marginBottom: 12 }}>Новая группа</h3>
        <div
          style={{
            display: 'flex',
            gap: 12,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <input
            placeholder="Название группы"
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
            style={{
              padding: 8,
              borderRadius: 6,
              border: '1px solid #e2e8f0',
              flex: 2,
            }}
          />
          <select
            value={newGroupCategory}
            onChange={(e) => handleNewCategoryChange(e.target.value)}
            style={{
              padding: 8,
              borderRadius: 6,
              border: '1px solid #e2e8f0',
              flex: 1,
            }}
          >
            <option value="">-- Категория --</option>
            <option value="одежда">Одежда</option>
            <option value="обувь">Обувь</option>
            <option value="аксессуары">Аксессуары</option>
          </select>
          <div style={{ flex: 2, minWidth: 200 }}>
            <input
              list="new-subcategory-list"
              value={newGroupSubcategory}
              onChange={(e) => setNewGroupSubcategory(e.target.value)}
              placeholder="Подкатегория"
              style={{
                width: '100%',
                padding: 8,
                borderRadius: 6,
                border: '1px solid #e2e8f0',
              }}
            />
            <datalist id="new-subcategory-list">
              {subcategoryOptionsForNew.map((sc) => (
                <option key={sc} value={sc} />
              ))}
            </datalist>
          </div>
          <button
            onClick={handleCreateGroup}
            className="btn btn-primary"
            style={{ padding: '6px 16px', opacity: creating ? 0.7 : 1 }}
            disabled={creating}
          >
            {creating ? 'Создание...' : 'Создать'}
          </button>
        </div>
      </div>

      {groups.length === 0 && (
        <p style={{ color: '#64748b' }}>Нет созданных групп.</p>
      )}

      {/* Список групп */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {groups.map((group) => (
          <div
            key={group.id}
            style={{
              background: 'white',
              borderRadius: 12,
              padding: 16,
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 10,
              }}
            >
              <div>
                <strong>{group.name}</strong>
                <span
                  style={{
                    marginLeft: 10,
                    color: '#7C3AED',
                    fontSize: 14,
                  }}
                >
                  ({group.subcategory})
                </span>
                <span
                  style={{
                    marginLeft: 10,
                    color: '#64748b',
                    fontSize: 13,
                  }}
                >
                  {group.products ? group.products.length : 0} товаров
                </span>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() =>
                    setSelectedGroup(
                      selectedGroup?.id === group.id ? null : group
                    )
                  }
                  className="btn btn-outline"
                  style={{ padding: '4px 12px' }}
                >
                  {selectedGroup?.id === group.id ? 'Скрыть' : 'Товары'}
                </button>
                <button
                  onClick={() => startEditGroup(group)}
                  className="btn btn-outline"
                  style={{ padding: '4px 12px' }}
                >
                  Ред.
                </button>
                <button
                  onClick={() => handleDeleteGroup(group.id)}
                  className="btn btn-outline"
                  style={{
                    color: 'red',
                    borderColor: 'red',
                    padding: '4px 12px',
                  }}
                >
                  Удалить
                </button>
              </div>
            </div>

            {/* Товары группы */}
            {selectedGroup?.id === group.id && (
              <div
                style={{
                  marginTop: 15,
                  borderTop: '1px solid #E2E8F0',
                  paddingTop: 12,
                }}
              >
                {group.products && group.products.length > 0 ? (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 8,
                    }}
                  >
                    {group.products.map((product) => (
                      <div
                        key={product.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '6px 0',
                          borderBottom: '1px solid #F1F5F9',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                          }}
                        >
                          <ProductThumbnail product={product} size={40} />
                          <span>
                            {product.title} ({product.platformSku})
                          </span>
                        </div>
                        <button
                          onClick={() =>
                            handleRemoveProductFromGroup(product.id)
                          }
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#EF4444',
                            cursor: 'pointer',
                            fontSize: 16,
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ color: '#94a3b8' }}>
                    В этой группе пока нет товаров.
                  </p>
                )}
                <button
                  onClick={() => setShowProductPicker(true)}
                  className="btn btn-primary"
                  style={{ marginTop: 10, padding: '6px 16px' }}
                >
                  + Добавить товар в склейку
                </button>
              </div>
            )}

            {/* Редактирование группы */}
            {editingGroup?.id === group.id && (
              <div
                style={{
                  marginTop: 15,
                  borderTop: '1px solid #E2E8F0',
                  paddingTop: 12,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    gap: 10,
                    flexWrap: 'wrap',
                    marginBottom: 8,
                    alignItems: 'center',
                  }}
                >
                  <input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    placeholder="Новое название"
                    style={{
                      padding: 6,
                      borderRadius: 6,
                      border: '1px solid #e2e8f0',
                      flex: 2,
                    }}
                  />
                  <select
                    value={editCategory}
                    onChange={(e) => handleEditCategoryChange(e.target.value)}
                    style={{
                      padding: 6,
                      borderRadius: 6,
                      border: '1px solid #e2e8f0',
                      flex: 1,
                    }}
                  >
                    <option value="">-- Категория --</option>
                    <option value="одежда">Одежда</option>
                    <option value="обувь">Обувь</option>
                    <option value="аксессуары">Аксессуары</option>
                  </select>
                  <div style={{ flex: 2, minWidth: 200 }}>
                    <input
                      list="edit-subcategory-list"
                      value={editSubcategory}
                      onChange={(e) => setEditSubcategory(e.target.value)}
                      placeholder="Подкатегория"
                      style={{
                        width: '100%',
                        padding: 6,
                        borderRadius: 6,
                        border: '1px solid #e2e8f0',
                      }}
                    />
                    <datalist id="edit-subcategory-list">
                      {subcategoryOptionsForEdit.map((sc) => (
                        <option key={sc} value={sc} />
                      ))}
                    </datalist>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    onClick={handleUpdateGroup}
                    className="btn btn-primary"
                    style={{ padding: '4px 12px' }}
                  >
                    Сохранить
                  </button>
                  <button
                    onClick={() => setEditingGroup(null)}
                    className="btn btn-outline"
                    style={{ padding: '4px 12px' }}
                  >
                    Отмена
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {showProductPicker && (
        <GroupProductPickerModal
          seller={seller}
          group={selectedGroup}
          onClose={() => setShowProductPicker(false)}
          onAssign={load}
        />
      )}
    </div>
  );
}

export default GroupsTab;